import { CartLinesUpdateEvent } from '@shopify/events';

const pricing = window.RedPowerPricing;
const isArabic = () => document.documentElement.lang?.startsWith('ar');
let cartStatePromise;

const getCart = (refresh = false) => {
  if (!cartStatePromise || refresh) {
    cartStatePromise = fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(response => response.json());
  }
  return cartStatePromise;
};

const cardMoney = (cents, currency) => {
  const value = new Intl.NumberFormat(document.documentElement.lang || 'en', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format((Number(cents) || 0) / 100);
  return currency === 'SAR' ? `${value} SAR` : `${currency} ${value}`;
};

const showToast = () => {
  document.querySelector('.rp-cart-toast')?.remove();
  const toast = document.createElement('div');
  toast.className = 'rp-cart-toast';
  toast.setAttribute('role', 'status');
  toast.textContent = isArabic() ? '✓ تمت إضافة المنتج إلى السلة' : '✓ Added to cart';
  document.body.appendChild(toast);
  setTimeout(() => toast.remove(), 2600);
};

const sameSchedule = (item, tiers) => {
  const itemTiers = pricing.getTiers(item, item.properties);
  return itemTiers && [1, 2, 3, 4].every(quantity => Number(itemTiers[quantity]) === Number(tiers[quantity]));
};

const initCard = async root => {
  if (root.dataset.ready) return;
  root.dataset.ready = '1';

  const title = root.dataset.productTitle || '';
  const handle = root.dataset.productHandle || '';
  const variantId = Number(root.dataset.variantId);
  const currency = root.dataset.currency || 'SAR';
  // The translated title changes between /products and /ar/products.
  // Shopify's handle does not, so always include it in the pricing identity.
  const product = { title, handle };
  const tiers = pricing?.getTiers(product);
  if (!tiers || !variantId) return;

  const card = root.closest('product-card, .force-product-card, .resource-card');
  if (!card) return;
  card.classList.add('rp-priced-card');
  root.hidden = false;

  const quantityBox = root.querySelector('[data-rp-quantity]');
  const totalBox = root.querySelector('[data-rp-total]');
  const unitBox = root.querySelector('[data-rp-unit]');
  const compareBox = root.querySelector('[data-rp-compare]');
  const discountBadge = root.querySelector('[data-rp-discount]');
  const addButton = root.querySelector('[data-rp-add]');
  const addLabel = root.querySelector('[data-rp-add-label]');
  const errorBox = root.querySelector('[data-rp-error]');
  const productPrice = Number(root.dataset.productPrice) || tiers[1];
  const comparePrice = Number(root.dataset.comparePrice) || 0;
  let quantity = 1;
  let cartLineKey = '';
  let inCart = false;
  let dirty = false;

  const label = state => {
    if (state === 'loading') return isArabic() ? 'جاري الإضافة…' : 'ADDING…';
    if (state === 'update') return isArabic() ? 'حدّث السلة' : 'UPDATE';
    if (state === 'added') return isArabic() ? 'تمت الإضافة ✓' : 'ADDED ✓';
    return isArabic() ? 'أضف للسلة' : 'ADD';
  };

  const render = () => {
    const total = pricing.getBundleTotal(product, quantity);
    const unit = pricing.getUnitPrice(product, quantity);
    const originalUnit = comparePrice > productPrice ? comparePrice : tiers[1];
    const originalTotal = originalUnit * quantity;
    quantityBox.textContent = String(quantity);
    totalBox.textContent = cardMoney(total, currency);
    unitBox.textContent = quantity > 1
      ? `${cardMoney(unit, currency)} ${isArabic() ? 'للعلبة' : 'each'}`
      : '';
    compareBox.hidden = originalTotal <= total;
    compareBox.textContent = cardMoney(originalTotal, currency);
    addLabel.textContent = dirty && cartLineKey ? label('update') : (inCart ? label('added') : label('add'));
    addButton.classList.toggle('is-added', inCart);

    if (comparePrice > productPrice) {
      const percent = Math.max(1, Math.round((1 - tiers[1] / comparePrice) * 100));
      discountBadge.textContent = `-${percent}%`;
      discountBadge.hidden = false;
    }
  };

  const syncCart = async () => {
    try {
      const cart = await getCart();
      const matching = cart.items.find(item => Number(item.variant_id || item.id) === variantId && sameSchedule(item, tiers));
      if (matching) {
        cartLineKey = matching.key;
        quantity = Number(matching.quantity) || 1;
        inCart = true;
        dirty = false;
      }
      render();
    } catch {
      render();
    }
  };

  root.querySelector('[data-rp-minus]').addEventListener('click', event => {
    event.preventDefault(); event.stopPropagation();
    quantity = Math.max(1, quantity - 1);
    if (cartLineKey) { inCart = false; dirty = true; addButton.classList.remove('is-added'); }
    render();
  });

  root.querySelector('[data-rp-plus]').addEventListener('click', event => {
    event.preventDefault(); event.stopPropagation();
    quantity += 1;
    if (cartLineKey) { inCart = false; dirty = true; addButton.classList.remove('is-added'); }
    render();
  });

  addButton.addEventListener('click', async event => {
    event.preventDefault(); event.stopPropagation();
    if (addButton.disabled || root.dataset.available !== 'true') return;
    errorBox.hidden = true;
    addButton.disabled = true;
    addLabel.textContent = label('loading');

    const deferred = CartLinesUpdateEvent.createPromise();
    root.dispatchEvent(new CartLinesUpdateEvent({
      action: cartLineKey ? 'update' : 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity }],
      promise: deferred.promise,
    }));

    try {
      const properties = {
        _force_bundle_price_1: tiers[1],
        _force_bundle_price_2: tiers[2],
        _force_bundle_price_3: tiers[3],
        _force_bundle_price_4: tiers[4],
      };
      const url = cartLineKey ? '/cart/change.js' : '/cart/add.js';
      const body = cartLineKey
        ? { id: cartLineKey, quantity }
        : { items: [{ id: variantId, quantity, properties }] };
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Cart update failed');

      const cart = await getCart(true);
      const matching = cart.items.find(item => Number(item.variant_id || item.id) === variantId && sameSchedule(item, tiers));
      cartLineKey = matching?.key || cartLineKey;
      quantity = Number(matching?.quantity) || quantity;
      inCart = true;
      dirty = false;
      render();
      showToast();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, productId: root.dataset.productHandle, didError: false },
      });
    } catch (error) {
      errorBox.textContent = error?.message || (isArabic() ? 'تعذر تحديث السلة' : 'Could not update cart');
      errorBox.hidden = false;
      addLabel.textContent = label(inCart ? 'added' : 'add');
      deferred.reject(error);
    } finally {
      addButton.disabled = false;
    }
  });

  render();
  await syncCart();
};

const initAll = scope => scope.querySelectorAll?.('[data-rp-grid-card]').forEach(initCard);
initAll(document);
document.addEventListener('shopify:section:load', event => initAll(event.target));
